import cx from "classnames";
import { useShallow } from "zustand/react/shallow";

import { type MainNavigationMenuItem } from "components/MainNavigation/types";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import useMainNavigationActiveStates from "components/MainNavigation/hooks/useActiveStates";
import Box from "ds/components/Box";
import { selectNavigationShowsExpandedContent } from "components/MainNavigation/selectors/navigationState";
import MainNavigationBaseActionLink from "components/MainNavigation/components/atoms/BaseAction/Link";
import MainNavigationCounter from "components/MainNavigation/components/atoms/Counter";
import MainNavigationBetaIndicator from "components/MainNavigation/components/atoms/BetaIndicator";
import MainNavigationPinAction from "components/MainNavigation/components/atoms/PinAction";

import styles from "./styles.module.css";

type MainNavigationGroupMenuItemProps = {
  item: MainNavigationMenuItem;
  className?: string;
};

const MainNavigationGroupMenuItem = ({ item, className }: MainNavigationGroupMenuItemProps) => {
  const { showsExpandedContent, itemCounts, isPinRevealed } = useMainNavigationStore(
    useShallow((state) => ({
      showsExpandedContent: selectNavigationShowsExpandedContent(state),
      itemCounts: state.itemCounts,
      isPinRevealed: state.revealedPinItemId === item.id,
    }))
  );

  const { activeMenuItemId } = useMainNavigationActiveStates();
  const isActive = item.id === activeMenuItemId;
  const itemCount = itemCounts[item.id] || 0;

  return (
    <Box
      fullWidth
      relative
      className={cx(styles.container, { [styles.pinRevealed]: isPinRevealed })}
    >
      <MainNavigationBaseActionLink
        id={item.htmlId}
        to={item.to}
        href={item.href}
        onClick={item.onAction}
        title={item.title}
        isActive={isActive}
        className={cx(styles.menuItem, className)}
        isSidebarExpanded={showsExpandedContent}
      >
        {item.beta ? (
          <MainNavigationBetaIndicator className={styles.counter} />
        ) : (
          <MainNavigationCounter count={itemCount} inversed={isActive} className={styles.counter} />
        )}
      </MainNavigationBaseActionLink>

      {showsExpandedContent && <MainNavigationPinAction item={item} className={styles.pin} />}
    </Box>
  );
};

export default MainNavigationGroupMenuItem;
